import { useState } from "react";
import { useTranslation } from "react-i18next";
// Redux
import { useSelector } from "react-redux";
// Constants - functions
import { pathHomePage, WEB_SERVICE_MIMS, applications } from "../../config/constants";
import { getAge, renderAvatar } from "../../config/functions";
import { allowGlobalAdherenceView, getApplicationPath, applicationsData } from "../../config/validateApplication";
// Components
import {
  PatientDiv,
  UserDiv,
  AvatarLink,
  AvatarImg,
  UserInfoDiv,
  UserText,
  UserInfoText,
  ShowAppsButton,
  ShowAppsImg,
  ProfileDiv,
  ProfileLink,
  ProfileImg,
  InformationDiv,
  ApplicationDiv,
  ApplicationProfileLink,
  ApplicationProfileImg,
  ApplicationLink,
  SoftwareFilterIndicator,
  ApplicationImg,
  AlertImg,
} from "./styles";
import Axios from "../../config/axios";
// Assets
import IcoProfile from "../../assets/icons/ico_patient_profile.svg";
import IcoArrowUp from "../../assets/icons/ico_arrow_up.svg";
import IcoArrowDown from "../../assets/icons/ico_arrow_down.svg";
import IcoRecsys from "../../assets/icons/recsys.svg";
import IcoAlert from "../../assets/icons/ico_alert1.svg";

const PatientData = ({
  patientIndex,
  patientId,
  isThereSomePrescription,
  isTherePrescriptionsAfter7Days,
  isThePrescriptionMissing,
  adherence,
  user,
  gender,
  birthDate,
  pathology,
  patientApplications,
  hospitalApplications,
  filteredSoftwareId,
  currentFilter,
  isAISNHospital,
}) => {
  const [showApps, setShowApps] = useState(false);
  const clinicianToken = useSelector(({ clinician }) => clinician.data.token);
  const clinicianId = useSelector(({ clinician }) => clinician.data.CLINICIAN_ID);
  const [t] = useTranslation("global");

  const getGender = () => {
    const genders = {
      MALE: "male",
      FEMALE: "female",
    };

    return genders[gender] ? t(`patientGender_${genders[gender]}`) : "";
  };

  const getPathology = () => {
    const pathologies = {
      Other: "other",
      "Alzheimer's": "alzheimer",
      Ataxia: "ataxia",
      "Cerebral Palsy": "cerebralPalsy",
      "Multiple Sclerosis": "multipleSclerosis",
      "Parkinson's": "parkinson",
      "Spinal Cord Injury": "spinalCordInjury",
      Stroke: "stroke",
      "Traumatic Brain Injury": "traumaticBrainInjury",
    };

    return pathologies[pathology] ? t(`patientPathology_${pathologies[pathology]}`) : "";
  };

  const renderUserInfo = () => {
    let userData = `${getGender()} ${getAge(birthDate)}`;
    if (pathology) userData += `, ${getPathology()}`;
    return userData;
  };

  const renderAISNSection = () => {
    const RGSwearId = applications.wear.id;
    const path = getApplicationPath(RGSwearId, clinicianToken, patientId);
    const { availableImg } = applicationsData[RGSwearId] || {};

    return (
      <>
        <ApplicationLink to={path}>
          <ApplicationImg src={availableImg} alt="ico-rgs-wear" />
        </ApplicationLink>
        <ApplicationLink to={`${pathHomePage}/recsys/${clinicianToken}/${patientId}`}>
          <ApplicationImg src={IcoRecsys} alt="ico-recsys" />
          {isThePrescriptionMissing && <AlertImg src={IcoAlert} alt="ico-alert" />}
        </ApplicationLink>
      </>
    );
  };

  const renderApplications = () => {
    return hospitalApplications.map((application) => {
      const applicationId = application.SOFTWARE_ID;
      const hasTheHospitalThisApplication = patientApplications.includes(applicationId);
      const path = getApplicationPath(applicationId, clinicianToken, patientId);
      const { availableImg, disableImg } = applicationsData[applicationId] || {};
      const display = currentFilter === "APPLICATIONS" && applicationId === filteredSoftwareId ? "flex" : "none";

      if (
        (applicationId === applications.app.id && patientApplications.includes(applications.plus.id)) ||
        (applicationId === applications.app.id && !patientApplications.includes(applications.app.id))
      ) {
        return null;
      }
      if (applicationId === applications.plus.id && patientApplications.includes(applications.app.id)) {
        return null;
      }

      return (
        <ApplicationLink key={applicationId} to={path} disabled={!hasTheHospitalThisApplication}>
          {!patientIndex && <SoftwareFilterIndicator display={display} />}
          <ApplicationImg src={hasTheHospitalThisApplication ? availableImg : disableImg} alt="software-icon" />
        </ApplicationLink>
      );
    });
  };

  const addPatientActivity = () => {
    const clinicianAndPatientData = {
      CLINICIAN_ID: clinicianId,
      PATIENT_ID: patientId,
    };
    Axios("post", `${WEB_SERVICE_MIMS}/clinicians/patient-activity`, clinicianAndPatientData).catch((error) => {
      console.log("Add patient activity: ", error);
    });
  };

  return (
    <PatientDiv>
      <UserDiv>
        <AvatarLink
          onClick={addPatientActivity}
          to={`${pathHomePage}/global-Patient-Adherence/${clinicianToken}/${patientId}`}
          disabled={!allowGlobalAdherenceView(patientApplications) || isAISNHospital}
        >
          <AvatarImg src={renderAvatar(isThereSomePrescription, isTherePrescriptionsAfter7Days, adherence)} alt="avatar" />
        </AvatarLink>
        <UserInfoDiv>
          <UserText>{user}</UserText>
          {/* Responsive view */}
          <UserInfoText>{renderUserInfo()}</UserInfoText>
        </UserInfoDiv>
        <ShowAppsButton onClick={() => setShowApps(!showApps)}>
          <ShowAppsImg src={showApps ? IcoArrowUp : IcoArrowDown} alt="ico-arrow" />
        </ShowAppsButton>
      </UserDiv>
      <ProfileDiv>
        <ProfileLink to={`${pathHomePage}/edit-patient/${clinicianToken}/${patientId}`}>
          <ProfileImg src={IcoProfile} alt="profile" />
        </ProfileLink>
      </ProfileDiv>
      {/* Desktop view */}
      <InformationDiv>
        <p>{renderUserInfo()}</p>
      </InformationDiv>
      <ApplicationDiv show={showApps}>
        <ApplicationProfileLink to={`${pathHomePage}/edit-patient/${clinicianToken}/${patientId}`}>
          <ApplicationProfileImg src={IcoProfile} alt="profile" />
        </ApplicationProfileLink>
        {isAISNHospital ? renderAISNSection() : renderApplications()}
      </ApplicationDiv>
    </PatientDiv>
  );
};

export default PatientData;
